import { useState, useCallback, useRef, useEffect } from 'react';
import { useAppStore } from '@/stores/appStore';
import { logger } from '@/lib/logger';
import { useAlignmentHelpers } from '@/hooks/useAlignmentHelpers';
import type { ElementPosition, SnapPoint } from '@/types/alignment';
import { useFloorPlanContext } from '../FloorPlanProvider';

type DraggedEntityType =
  | 'fixture'
  | 'control'
  | 'panel'
  | 'room'
  | 'product'
  | 'annotation'
  | 'cableJunction';

interface DragState {
  type: DraggedEntityType;
  id: string;
  startPos: { x: number; y: number };
  currentPos: { x: number; y: number };
  hasMovedThreshold: boolean; // Track if we've moved enough to be considered dragging
  initialElementPos?: { x: number; y: number }; // Initial position of the element being dragged
  clickOffset?: { x: number; y: number }; // Offset from element position to click position
  snapPoints?: SnapPoint[]; // Active snap points for visual guides
}

interface UseDragHandlerProps {
  containerRef: React.RefObject<HTMLDivElement>;
  onUpdatePosition: (id: string, x: number, y: number, type: DraggedEntityType) => Promise<void>;
  allElements?: ElementPosition[]; // Optional: pre-collected element positions for snap
}

type DragDocumentEvent = MouseEvent | TouchEvent | PointerEvent;

const isSnappableElementType = (type: DraggedEntityType): type is ElementPosition['type'] =>
  type === 'fixture' ||
  type === 'control' ||
  type === 'product' ||
  type === 'annotation' ||
  type === 'room';

export function useDragHandler({
  containerRef,
  onUpdatePosition,
  allElements = [],
}: UseDragHandlerProps) {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false); // Track mouse down state
  const [recentlyDraggedElements, setRecentlyDraggedElements] = useState<Set<string>>(new Set()); // Track recently dragged elements
  const dragStateRef = useRef<DragState | null>(null);
  const isDraggingRef = useRef(false);
  const isPointerDownRef = useRef(false);
  const isEndingDragRef = useRef(false);
  const dragAnimationFrameRef = useRef<number>(0);
  const lastBroadcastTimeRef = useRef<number>(0);
  const releaseSafetyCleanupRef = useRef<(() => void) | null>(null);
  const endDragRef = useRef<(() => Promise<void>) | null>(null);
  const updateDragRef = useRef<((e: DragDocumentEvent) => void) | null>(null);

  const viewport = useAppStore(state => state.floorPlan.viewport);
  const activeTool = useAppStore(state => state.ui.toolbar.activeTool);

  // Get broadcast functions from context
  const { broadcastFunctions } = useFloorPlanContext();

  // Alignment and snap functionality
  const { calculateSnap } = useAlignmentHelpers();

  const resetDragState = useCallback(() => {
    releaseSafetyCleanupRef.current?.();
    releaseSafetyCleanupRef.current = null;

    dragStateRef.current = null;
    isDraggingRef.current = false;
    isPointerDownRef.current = false;
    isEndingDragRef.current = false;

    setDragState(null);
    setIsDragging(false);
    setIsPointerDown(false);

    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.classList.remove('dragging');

    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = 0;
    }
  }, []);

  // Convert screen coordinates to image coordinates
  const screenToImageCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return { x: 0, y: 0 };

      const rect = container.getBoundingClientRect();

      // Screen position relative to container
      const screenX = clientX - rect.left;
      const screenY = clientY - rect.top;

      // Convert to image coordinates accounting for zoom and pan
      const imageX = (screenX - viewport.positionX) / viewport.scale;
      const imageY = (screenY - viewport.positionY) / viewport.scale;

      return { x: imageX, y: imageY };
    },
    [containerRef, viewport],
  );

  // Start potential dragging (on mouse down or touch start)
  const startDrag = useCallback(
    (
      type: DraggedEntityType,
      id: string,
      e: React.MouseEvent | React.TouchEvent | React.PointerEvent | PointerEvent,
      elementPos?: { x: number; y: number },
    ) => {
      // Don't start drag if hand tool is active
      if (activeTool === 'pan') {
        return;
      }

      // Check for multi-touch and bail out if detected
      if ('touches' in e && e.touches.length > 1) {
        return; // Don't start dragging during multi-touch (pinch-to-zoom)
      }

      // CRITICAL: Immediately prevent default to stop browser scrolling
      e.preventDefault();
      e.stopPropagation();

      // For touch events, prevent any default browser behavior
      if ('touches' in e && e.nativeEvent) {
        e.nativeEvent.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }

      // Get coordinates from either mouse or touch event
      const clientX = 'touches' in e ? e.touches[0].clientX : 'clientX' in e ? e.clientX : 0;
      const clientY = 'touches' in e ? e.touches[0].clientY : 'clientY' in e ? e.clientY : 0;

      const { x, y } = screenToImageCoordinates(clientX, clientY);

      // Calculate click offset if element position is provided
      let clickOffset = { x: 0, y: 0 };
      if (elementPos) {
        clickOffset = {
          x: x - elementPos.x,
          y: y - elementPos.y,
        };
      }

      // Set up potential drag state but don't mark as actively dragging yet
      const nextDragState: DragState = {
        type,
        id,
        startPos: { x, y },
        currentPos: { x, y },
        hasMovedThreshold: false,
        initialElementPos: elementPos,
        clickOffset,
      };

      dragStateRef.current = nextDragState;
      isDraggingRef.current = false;
      isPointerDownRef.current = true;
      isEndingDragRef.current = false;

      setDragState(nextDragState);
      setIsPointerDown(true);

      // Install release listeners synchronously. Waiting for the `isPointerDown`
      // effect can miss a very quick mouseup/touchend, leaving the element
      // attached to the cursor after click-release.
      releaseSafetyCleanupRef.current?.();

      const handleRelease = (event: DragDocumentEvent) => {
        event.preventDefault();
        endDragRef.current?.();
      };
      const handleMove = (event: DragDocumentEvent) => {
        event.preventDefault();
        updateDragRef.current?.(event);
      };

      document.addEventListener('mousemove', handleMove, { capture: true, passive: false });
      document.addEventListener('mouseup', handleRelease, { capture: true, passive: false });
      document.addEventListener('pointermove', handleMove, { capture: true, passive: false });
      document.addEventListener('pointerup', handleRelease, { capture: true, passive: false });
      document.addEventListener('pointercancel', handleRelease, { capture: true, passive: false });
      document.addEventListener('touchmove', handleMove, { capture: true, passive: false });
      document.addEventListener('touchend', handleRelease, { capture: true, passive: false });
      document.addEventListener('touchcancel', handleRelease, { capture: true, passive: false });

      releaseSafetyCleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMove, { capture: true });
        document.removeEventListener('mouseup', handleRelease, { capture: true });
        document.removeEventListener('pointermove', handleMove, { capture: true });
        document.removeEventListener('pointerup', handleRelease, { capture: true });
        document.removeEventListener('pointercancel', handleRelease, { capture: true });
        document.removeEventListener('touchmove', handleMove, { capture: true });
        document.removeEventListener('touchend', handleRelease, { capture: true });
        document.removeEventListener('touchcancel', handleRelease, { capture: true });
      };

      logger.log(
        `Pointer down on ${type} ${id} at`,
        { x, y },
        'elementPos:',
        elementPos,
        'clickOffset:',
        clickOffset,
      );
    },
    [screenToImageCoordinates, activeTool],
  );

  // Update drag position (mouse or touch)
  const updateDrag = useCallback(
    (e: DragDocumentEvent) => {
      const currentDragState = dragStateRef.current;
      if (!currentDragState || !isPointerDownRef.current || isEndingDragRef.current) return;

      // If a mouseup was missed, the next mousemove arrives with no buttons pressed.
      // Treat that as a release instead of letting the element follow the cursor.
      if (!('touches' in e) && e.buttons === 0) {
        endDragRef.current?.();
        return;
      }

      // Check for multi-touch and stop dragging if detected
      if ('touches' in e && e.touches.length > 1) {
        // Multi-touch detected, end the drag
        resetDragState();
        return;
      }

      // Prevent default to stop browser scrolling on touch devices
      if ('touches' in e) {
        e.preventDefault();
      }

      // Get coordinates from either mouse or touch event
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const { x, y } = screenToImageCoordinates(clientX, clientY);

      // Calculate distance moved from start position
      const deltaX = x - currentDragState.startPos.x;
      const deltaY = y - currentDragState.startPos.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Only start actual dragging if we've moved beyond a threshold (5 pixels)
      const DRAG_THRESHOLD = 5;
      const shouldStartDragging = distance > DRAG_THRESHOLD && !currentDragState.hasMovedThreshold;

      // Calculate snap before updating state
      let activeSnapPoints: SnapPoint[] = [];

      // For annotations, use click offset to ensure element follows cursor exactly
      // For other elements, use the delta from start position
      let finalX: number;
      let finalY: number;

      if (currentDragState.type === 'annotation' && currentDragState.clickOffset) {
        // Annotation: position = cursor position - click offset (where user clicked on the element)
        finalX = x - currentDragState.clickOffset.x;
        finalY = y - currentDragState.clickOffset.y;
      } else if (currentDragState.initialElementPos) {
        // Other elements: position = initial position + delta
        finalX = currentDragState.initialElementPos.x + (x - currentDragState.startPos.x);
        finalY = currentDragState.initialElementPos.y + (y - currentDragState.startPos.y);
      } else {
        finalX = x;
        finalY = y;
      }

      // Apply snap if enabled and element position data is available
      // Skip snap for annotations - they should move freely
      const skipSnap =
        currentDragState.type === 'annotation' || !isSnappableElementType(currentDragState.type);
      if (
        !skipSnap &&
        currentDragState.initialElementPos &&
        allElements.length > 0 &&
        (currentDragState.hasMovedThreshold || shouldStartDragging)
      ) {
        const element = document.querySelector(
          `[data-${currentDragState.type}-id="${currentDragState.id}"]`,
        ) as HTMLElement;
        if (element) {
          // Get current dragged element info from allElements (more reliable than DOM dataset)
          const existingElement = allElements.find(el => el.id === currentDragState.id);
          const elementWidth = existingElement?.width || parseFloat(element.dataset?.width || '40');
          const elementHeight =
            existingElement?.height || parseFloat(element.dataset?.height || '40');

          const draggedElement: ElementPosition = {
            id: currentDragState.id,
            type: currentDragState.type,
            x: finalX,
            y: finalY,
            width: elementWidth,
            height: elementHeight,
          };

          // Calculate snap
          const snapResult = calculateSnap(
            draggedElement,
            allElements.filter(el => el.id !== currentDragState.id), // Exclude self
            { x: finalX, y: finalY },
          );

          // Use snapped position if snapping occurred
          if (snapResult.snapped) {
            finalX = snapResult.x;
            finalY = snapResult.y;
            activeSnapPoints = snapResult.snapPoints;
          }
        }
      }

      // Update drag state with new position and snap points
      const nextDragState = {
        ...currentDragState,
        currentPos: { x: finalX, y: finalY },
        hasMovedThreshold: currentDragState.hasMovedThreshold || distance > DRAG_THRESHOLD,
        snapPoints: activeSnapPoints,
      };
      dragStateRef.current = nextDragState;
      setDragState(nextDragState);

      // Start visual dragging if we've crossed the threshold
      if (shouldStartDragging) {
        isDraggingRef.current = true;
        setIsDragging(true);

        // Apply visual feedback
        const element = document.querySelector(
          `[data-${currentDragState.type}-id="${currentDragState.id}"]`,
        ) as HTMLElement;
        if (element) {
          element.setAttribute('data-dragging', 'true');
          element.style.transition = 'none';
          // Only add shadow for non-SVG elements (strips use SVG, shadow causes white rectangle)
          const hasSvg = element.querySelector('svg');
          if (!hasSvg) {
            element.classList.add('shadow-lg');
          }
          element.classList.add('z-[100]');
        }

        // Global cursor
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
        document.body.classList.add('dragging');

        logger.log(`Started actual dragging ${currentDragState.type} ${currentDragState.id}`);
      }

      // Only update visual position if we're actually dragging
      if (currentDragState.hasMovedThreshold || shouldStartDragging) {
        // Throttle updates with requestAnimationFrame
        if (dragAnimationFrameRef.current) {
          cancelAnimationFrame(dragAnimationFrameRef.current);
        }

        dragAnimationFrameRef.current = requestAnimationFrame(() => {
          // Update visual position immediately
          const element = document.querySelector(
            `[data-${currentDragState.type}-id="${currentDragState.id}"]`,
          ) as HTMLElement;
          if (element) {
            // Store image coordinates for later persistence (if element supports dataset)
            if (element.dataset) {
              element.dataset.xPosition = String(finalX);
              element.dataset.yPosition = String(finalY);
            }

            // Update visual position directly using image coordinates (only for HTML elements)
            if (element.style) {
              element.style.left = `${finalX}px`;
              element.style.top = `${finalY}px`;
            }
          }

          // Broadcast drag position to other users (throttled to max 10x/sec)
          const now = Date.now();
          const timeSinceLastBroadcast = now - lastBroadcastTimeRef.current;
          const BROADCAST_THROTTLE_MS = 100; // 100ms = 10 broadcasts per second

          if (timeSinceLastBroadcast >= BROADCAST_THROTTLE_MS && broadcastFunctions) {
            lastBroadcastTimeRef.current = now;
            broadcastFunctions
              .broadcastDrag(currentDragState.type, currentDragState.id, finalX, finalY)
              .catch(err => {
                logger.error('[Broadcast] Failed to broadcast drag:', err);
              });
          }
        });
      }
    },
    [screenToImageCoordinates, allElements, calculateSnap, broadcastFunctions, resetDragState],
  );

  updateDragRef.current = updateDrag;

  // Function to mark an element as recently dragged
  const markAsRecentlyDragged = useCallback((elementId: string) => {
    setRecentlyDraggedElements(prev => new Set(prev).add(elementId));

    // Clear the flag after a short delay to allow click events to complete
    setTimeout(() => {
      setRecentlyDraggedElements(prev => {
        const newSet = new Set(prev);
        newSet.delete(elementId);
        return newSet;
      });
    }, 100); // 100ms delay should be enough for click events to fire
  }, []);

  // End dragging or click
  const endDrag = useCallback(async () => {
    const currentDragState = dragStateRef.current;
    if (!currentDragState || isEndingDragRef.current) return;

    isEndingDragRef.current = true;

    const { type, id, currentPos, hasMovedThreshold } = currentDragState;
    const wasActuallyDragging = isDraggingRef.current && hasMovedThreshold;

    const broadcastDragEnd = () => {
      if (broadcastFunctions) {
        broadcastFunctions.broadcastDragEnd(type, id).catch(err => {
          logger.error('[Broadcast] Failed to broadcast drag end:', err);
        });
      }
    };

    // Clean up visual feedback if we were actually dragging
    if (wasActuallyDragging) {
      const element = document.querySelector(`[data-${type}-id="${id}"]`) as HTMLElement;
      if (element) {
        element.removeAttribute('data-dragging');
        element.style.transition = '';
        // Remove shadow-lg only if it was added (non-SVG elements)
        const hasSvg = element.querySelector('svg');
        if (!hasSvg) {
          element.classList.remove('shadow-lg');
        }
        element.classList.remove('z-[100]');
      }

      // Calculate final position
      let finalX, finalY;

      if (type === 'annotation') {
        // For annotations, currentPos already contains the correct element position
        // calculated in updateDrag (cursor position - click offset)
        finalX = currentPos.x;
        finalY = currentPos.y;
      } else if (element?.dataset.xPosition) {
        // For HTML elements, use the dataset position
        finalX = parseFloat(element.dataset.xPosition);
        finalY = parseFloat(element.dataset.yPosition!);
      } else {
        // Fallback to current position
        finalX = currentPos.x;
        finalY = currentPos.y;
      }

      // For annotations, we need to update the position BEFORE clearing dragState
      // to prevent flickering (the visual drag position relies on dragState)
      // For other elements (fixtures, controls, etc.), they use DOM updates directly
      if (type === 'annotation') {
        const savePromise = onUpdatePosition(id, finalX, finalY, type)
          .then(() => {
            logger.log(`Saved ${type} ${id} final position:`, { x: finalX, y: finalY });
          })
          .catch(error => {
            logger.error(`Failed to save ${type} position:`, error);
          });

        resetDragState();
        markAsRecentlyDragged(id);
        broadcastDragEnd();

        await savePromise;
      } else {
        // For other element types, use the original async pattern
        // (they don't rely on dragState for visual positioning)
        resetDragState();
        markAsRecentlyDragged(id);

        setTimeout(async () => {
          try {
            await onUpdatePosition(id, finalX, finalY, type);
            logger.log(`Saved ${type} ${id} final position:`, { x: finalX, y: finalY });

            // Broadcast drag end AFTER database save completes
            broadcastDragEnd();
          } catch (error) {
            logger.error(`Failed to save ${type} position:`, error);
            // Still broadcast drag end even if save failed
            broadcastDragEnd();
          }
        }, 0);
      }
    } else {
      logger.log(`${type} ${id} was clicked but not dragged - no position update needed`);
      // Reset states for click (no drag)
      resetDragState();
    }

    logger.log('Drag ended');
  }, [broadcastFunctions, markAsRecentlyDragged, onUpdatePosition, resetDragState]);

  endDragRef.current = endDrag;

  // Function to check if an element was recently dragged
  const wasRecentlyDragged = useCallback(
    (elementId: string): boolean => {
      return recentlyDraggedElements.has(elementId);
    },
    [recentlyDraggedElements],
  );

  // Global mouse and touch event handlers
  useEffect(() => {
    if (!isPointerDown) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      updateDrag(e);
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      endDrag();
    };

    const handlePointerMove = (e: PointerEvent) => {
      e.preventDefault();
      updateDrag(e);
    };

    const handlePointerUp = (e: PointerEvent) => {
      e.preventDefault();
      endDrag();
    };

    const handleTouchStart = (e: TouchEvent) => {
      // Immediately prevent default to claim touch
      e.preventDefault();
      e.stopPropagation();
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Always prevent default first
      e.preventDefault();
      e.stopPropagation();

      // Check for multi-touch
      if (e.touches.length > 1) {
        // Multi-touch detected, end drag immediately
        endDrag();
        return;
      }

      if (e.touches.length === 1) {
        updateDrag(e);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      endDrag();
    };

    const handleWindowBlur = () => {
      endDrag();
    };

    // Add event listeners with capture to ensure we get them first
    document.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
    document.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });
    document.addEventListener('pointermove', handlePointerMove, { capture: true, passive: false });
    document.addEventListener('pointerup', handlePointerUp, { capture: true, passive: false });
    document.addEventListener('pointercancel', handlePointerUp, { capture: true, passive: false });
    document.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
    document.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
    document.addEventListener('touchend', handleTouchEnd, { capture: true, passive: false });
    document.addEventListener('touchcancel', handleTouchEnd, { capture: true, passive: false });
    window.addEventListener('blur', handleWindowBlur);

    // Also listen for escape key to cancel dragging
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        endDrag();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.removeEventListener('pointermove', handlePointerMove, { capture: true });
      document.removeEventListener('pointerup', handlePointerUp, { capture: true });
      document.removeEventListener('pointercancel', handlePointerUp, { capture: true });
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      document.removeEventListener('touchend', handleTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', handleTouchEnd, { capture: true });
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPointerDown, updateDrag, endDrag]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dragStateRef.current = null;
      isDraggingRef.current = false;
      isPointerDownRef.current = false;
      isEndingDragRef.current = false;

      if (dragAnimationFrameRef.current) {
        cancelAnimationFrame(dragAnimationFrameRef.current);
        dragAnimationFrameRef.current = 0;
      }

      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('dragging');

      releaseSafetyCleanupRef.current?.();
      releaseSafetyCleanupRef.current = null;
    };
  }, []);

  return {
    isDragging,
    dragState,
    startDrag,
    endDrag,
    wasRecentlyDragged,
    markAsRecentlyDragged,
    screenToImageCoordinates,
  };
}
